import React from 'react';
import {
  Check, Minus, X, HelpCircle, ExternalLink, Info,
} from 'lucide-react';
import { useJazyk } from '../../context/LanguageContext';

/**
 * Praktické údaje o zemi – co v ní pro trans lidi platí.
 *
 * Tohle je obsah, kvůli kterému má smysl na zemi vůbec klikat. Seznam
 * organizací sám o sobě neodpoví na to, co člověk před cestou nebo stěhováním
 * doopravdy řeší.
 *
 * Tři věci, na kterých tady záleží víc než na vzhledu:
 *
 *   1. **Nevyplněné není „ne".** Údaj, který nikdo neověřil, se ukazuje jako
 *      „nevíme". Kdyby se tvářil jako „nejde to", odradil by člověka od země,
 *      kam mohl klidně jet.
 *   2. **U každého údaje je vidět zdroj a datum.** Právní a zdravotnické
 *      informace stárnou; bez toho, odkud jsou a jak jsou staré, se nedají
 *      použít.
 *   3. **Není to právní poradenství** a je to napsané natvrdo dole, ne
 *      schované v podmínkách.
 */

// Názvy položek jsou ve slovníku (`profil.*`) – tady zůstává jen to, co se
// nepřekládá: pořadí a poznámka, u které položky platí obrácená logika.
const POLOZKY = {
  name_change: {
    otazka: 'Dá se úředně změnit jméno?',
  },
  gender_marker: {
    otazka: 'Dá se změnit pohlaví v dokladech?',
  },
  surgery_required: {
    otazka: 'Vyžaduje se k úřední změně operace nebo sterilizace?',
    // U téhle položky je „ano" špatná zpráva, ne dobrá. Proto má obrácené barvy.
    obracene: true,
  },
  healthcare: {
    otazka: 'Je dostupná hormonální a chirurgická péče?',
  },
  legal_protection: {
    otazka: 'Chrání zákon před diskriminací kvůli genderové identitě?',
  },
  safety: {
    otazka: 'Jak bezpečné to tam pro trans lidi je?',
  },
};

const STAVY = {
  yes: { icon: Check, barva: 'text-bloom-mint', pozadi: 'bg-bloom-mint/10' },
  limited: { icon: Minus, barva: 'text-amber-600', pozadi: 'bg-amber-50' },
  no: { icon: X, barva: 'text-red-600', pozadi: 'bg-red-50' },
  unknown: { icon: HelpCircle, barva: 'text-bloom-sub', pozadi: 'bg-bloom-bg' },
};

// U položky s obrácenou logikou („vyžaduje se operace?") je „ano" ta horší
// zpráva. Barvy se prohodí, aby na první pohled platilo zelená = dobré.
const OBRACENE = { yes: 'no', no: 'yes', limited: 'limited', unknown: 'unknown' };

const datum = (iso, jazyk) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(jazyk === 'en' ? 'en-GB' : 'cs-CZ',
      { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return ''; }
};

export default function CountryProfile({ zeme }) {
  const { jazyk, t, dvojjazycne } = useJazyk();
  const facts = zeme?.facts || {};
  const vyplnene = Object.keys(POLOZKY).filter((k) => facts[k]?.status
    && facts[k].status !== 'unknown');
  const uvod = dvojjazycne(zeme?.intro_cs, zeme?.intro_en);

  // Když není vyplněné vůbec nic, nemá smysl ukazovat šest krát „nevíme".
  // Prázdná tabulka vypadá jako chyba a nic neříká.
  if (!uvod && vyplnene.length === 0) return null;

  return (
    <section className="mt-6" data-testid="profil-zeme">
      {uvod && (
        <p className="text-bloom-text leading-relaxed whitespace-pre-line">{uvod}</p>
      )}

      {vyplnene.length > 0 && (
        <>
          <h2 className="mt-6 font-heading text-lg font-bold text-bloom-text">
            {t('profil.coTuPlati')}
          </h2>
          <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
            {Object.entries(POLOZKY).map(([klic, polozka]) => {
              const udaj = facts[klic];
              if (!udaj?.status || udaj.status === 'unknown') return null;
              const stav = STAVY[polozka.obracene
                ? OBRACENE[udaj.status]
                : udaj.status] || STAVY.unknown;
              const Ikona = stav.icon;
              // Doklady. Nově je každý záznam s vlastním rokem a názvem;
              // starší podoba byla jen holá adresa, tak se přijímá obojí.
              const doklady = (udaj.sources || [])
                .map((z) => (typeof z === 'string' ? { url: z } : z))
                .filter((z) => /^https?:\/\//i.test(z?.url || ''));
              if (doklady.length === 0 && /^https?:\/\//i.test(udaj.source || '')) {
                doklady.push({ url: udaj.source, year: udaj.source_year });
              }
              const zmena = udaj.recent_change;
              return (
                <div key={klic}
                     className="rounded-2xl border border-border/40 bg-white p-3.5"
                     data-testid={`profil-${klic}`}>
                  <div className="flex items-start gap-2.5">
                    <span className={`shrink-0 rounded-lg p-1.5 ${stav.pozadi}`}>
                      <Ikona className={`w-4 h-4 ${stav.barva}`} />
                    </span>
                    <div className="min-w-0">
                      <p className="font-medium text-bloom-text text-sm">
                        {t(`profil.${klic}`)}
                      </p>
                      <p className={`text-xs font-medium ${stav.barva}`}>
                        {t(`profil.stav.${udaj.status}`)}
                      </p>
                    </div>
                  </div>
                  {dvojjazycne(udaj.note_cs, udaj.note_en) && (
                    <p className="mt-2 text-sm text-bloom-text leading-relaxed">
                      {dvojjazycne(udaj.note_cs, udaj.note_en)}
                    </p>
                  )}
                  {/* Nedávná změna je samostatná věc, ne věta přilepená k poznámce:
                      má vlastní datum účinnosti a vlastní doklady. */}
                  {dvojjazycne(zmena?.note_cs, zmena?.note_en) && (
                    <div className="mt-2.5 rounded-lg bg-bloom-violet/5 border border-bloom-violet/15 px-2.5 py-2">
                      <p className="text-[11px] font-semibold text-bloom-violet uppercase tracking-wide">
                        {t('profil.zmena')}
                        {zmena.effective_date && (
                          <span className="font-normal normal-case opacity-80">
                            {' · '}{t('profil.odKdy', { datum: datum(zmena.effective_date, jazyk) })}
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 text-sm text-bloom-text leading-relaxed">
                        {dvojjazycne(zmena.note_cs, zmena.note_en)}
                      </p>
                    </div>
                  )}

                  {(doklady.length > 0 || udaj.checked_at) && (
                    <div className="mt-2 text-[11px] text-bloom-sub/80 space-y-0.5">
                      {doklady.map((z) => (
                        <p key={z.url}>
                          <a href={z.url} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-start gap-1 hover:text-bloom-violet">
                            <ExternalLink className="w-3 h-3 shrink-0 mt-0.5" />
                            <span>
                              {z.label || t('profil.zdroj')}
                              {z.year ? ` (${z.year})` : ''}
                              {/* Zákon nebo rozhodnutí soudu váží jinak než přehled
                                  organizace – a čtenář to má vědět. */}
                              {z.kind === 'primary' && (
                                <span className="ml-1 text-bloom-violet">· {t('profil.zakon')}</span>
                              )}
                            </span>
                          </a>
                        </p>
                      ))}
                      {udaj.checked_at && (
                        <p>{t('profil.overeno', { datum: datum(udaj.checked_at, jazyk) })}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <p className="mt-3 text-xs text-bloom-sub flex items-start gap-1.5 leading-relaxed">
            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            {t('profil.upozorneni')}
          </p>
        </>
      )}
    </section>
  );
}

export { POLOZKY, STAVY };
